import type { Question } from '@new_project/shared';

interface QuestionFieldProps {
  question: Question;
  value: unknown;
  onChange: (name: string, value: unknown) => void;
  /** When set, the control is locked (forced) — used for dependency-forced toggles. */
  disabled?: boolean | undefined;
  /** Extra hint shown under the field (e.g. "Required by backend" or the slug's use). */
  note?: string | undefined;
}

/** Humanize a copier question key: `project_name` → `Project name`. */
function humanize(name: string): string {
  const spaced = name.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Renders a single copier question as the appropriate form control. */
export function QuestionField({ question, value, onChange, disabled, note }: QuestionFieldProps) {
  const { name, type, help, choices } = question;
  const id = `q-${name}`;
  const noteEl = note ? <span className="block text-sm text-warning">{note}</span> : null;

  if (type === 'bool') {
    return (
      <label htmlFor={id} className="flex items-start gap-3 py-2">
        <input
          id={id}
          type="checkbox"
          className="mt-1 h-4 w-4"
          checked={value === true}
          disabled={disabled}
          onChange={(e) => onChange(name, e.target.checked)}
        />
        <span className={disabled ? 'opacity-80' : undefined}>
          <span className="font-medium">{humanize(name)}</span>
          {help ? <span className="block text-sm opacity-70">{help}</span> : null}
          {noteEl}
        </span>
      </label>
    );
  }

  return (
    <div className="py-2">
      <label htmlFor={id} className="mb-1 block font-medium">
        {humanize(name)}
        {note ? <span className="ml-1 font-normal text-gray-500 text-sm">({note})</span> : null}
      </label>
      {help ? <p className="mb-1 text-sm opacity-70">{help}</p> : null}

      {choices ? (
        <select
          id={id}
          className="w-full rounded border border-gray-300 px-3 py-2"
          value={value == null ? '' : String(value)}
          disabled={disabled}
          onChange={(e) => onChange(name, e.target.value)}
        >
          {choices.map((c) => (
            <option key={String(c.value)} value={String(c.value)}>
              {c.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={id}
          type={type === 'int' || type === 'float' ? 'number' : 'text'}
          className="w-full rounded border border-gray-300 px-3 py-2"
          value={value == null ? '' : String(value)}
          disabled={disabled}
          placeholder={typeof question.default === 'string' ? question.default : undefined}
          onChange={(e) => {
            const raw = e.target.value;
            if (type === 'int') onChange(name, raw === '' ? undefined : Number.parseInt(raw, 10));
            else if (type === 'float')
              onChange(name, raw === '' ? undefined : Number.parseFloat(raw));
            else onChange(name, raw);
          }}
        />
      )}
    </div>
  );
}
